/**
 * Helpers for transforming the curriculum-service `unit` payload into the
 * shapes consumed by the UnitV3 page component.
 *
 * The new curriculum-service shape for a unit can include:
 *   - lessons[]              (existing accordion-style lesson references)
 *   - linkSummaries[]        (new: summary entries pointing to lessons / pages)
 *   - trainingLinkSummary    (new: a single summary entry for PD training)
 *   - pageDisplayType        (boolean toggle indicating the new "lessonPage"
 *                             summary-list style)
 */

/**
 * Returns true when the unit has lesson references in the legacy `lessons`
 * field. Used to decide whether the accordion-style render is appropriate
 * vs. a flattened summaries render (the flattened render is future work).
 */
export function hasLegacyLessons(unit) {
  return Array.isArray(unit?.lessons) && unit.lessons.length > 0
}

/**
 * `pageDisplayType` is modeled in Contentful as a Boolean. When true we
 * treat the unit as a "lessonPage".
 */
export function isLessonPageDisplay(unit) {
  return unit?.pageDisplayType === true
}

function isSummaryEntry(section) {
  return section?.type === 'Summary' || section?.contentType === 'Summary'
}

function isSameSummary(summaryA, summaryB) {
  if (!summaryA || !summaryB) return false
  if (summaryA?.id && summaryB?.id) return summaryA.id === summaryB.id
  if (summaryA?.route && summaryB?.route) return summaryA.route === summaryB.route
  return false
}

/**
 * Unit pages from curriculum-service can provide lesson summaries in either:
 *  - `unit.linkSummaries` (legacy-compatible transformed shape), or
 *  - `unit.sections[]` entries where `type === "Summary"` (normalized shape).
 *
 * We normalize both sources into a single list of lesson summaries and remove
 * the training summary entry so callers can render lesson cards separately
 * from the "Teaching the Unit" CTA card.
 */
export function extractLinkSummariesFromUnit({ unit, trainingLinkSummary }) {
  const summariesFromUnit = Array.isArray(unit?.linkSummaries)
    ? unit.linkSummaries
    : Array.isArray(unit?.linkSummariesCollection)
      ? unit.linkSummariesCollection
      : []

  const summariesFromSections = Array.isArray(unit?.sections)
    ? unit.sections.filter(isSummaryEntry)
    : []

  const mergedSummaries =
    summariesFromUnit.length > 0 ? summariesFromUnit : summariesFromSections

  if (!trainingLinkSummary) return mergedSummaries

  return mergedSummaries.filter(
    summary => !isSameSummary(summary, trainingLinkSummary),
  )
}

/**
 * Resolves the training summary from either transformed metadata
 * (`trainingLinkSummary`) or the explicit `Summary` section passed in by
 * curriculum-service.
 */
export function resolveTrainingLinkSummary(unit) {
  if (unit?.trainingLinkSummary) return unit.trainingLinkSummary
  if (!Array.isArray(unit?.sections)) return null

  const trainingSection = unit.sections.find(
    section =>
      isSummaryEntry(section) &&
      typeof section?.route === 'string' &&
      section.route.includes('teaching-the-unit'),
  )

  return trainingSection || null
}

/**
 * Prepares link summaries for the future flattened-summary render path.
 * For now this is used only as part of the console.log payload so that
 * the shape is easy to inspect when verifying the curriculum-service
 * response.
 */
export function prepareItemsForSummaryList(linkSummaries) {
  if (!Array.isArray(linkSummaries)) return []

  return linkSummaries.map(item => ({
    title: item?.displayTitle,
    description: item?.description,
    path: item?.route,
    isPreview: item?.isPreview,
    markedComplete: item?.lessonComplete,
    tags: Array.isArray(item?.metadata)
      ? item.metadata
          .map(tag =>
            tag?.goal && typeof tag.goal === 'string'
              ? tag.goal.toUpperCase()
              : null,
          )
          .filter(Boolean)
      : [],
  }))
}

/**
 * Extracts the verb (`teach` | `preview`) from a react-router match.
 */
export function getPresentationMode(match) {
  return match?.params?.verb
}

function isDigitalWellBeingUnit({ unit, productName }) {
  if (typeof productName === 'string') {
    return productName === 'digital-well-being'
  }

  // Backward-compatible default for existing UnitV3 call sites/tests.
  // Reusable call sites should pass productName explicitly.
  if (productName === undefined) {
    return true
  }

  const courseCodeCandidates = [
    unit?.courseCode,
    unit?.course_code,
    unit?.courseContent?.courseCode,
    unit?.courseContent?.course_code,
    unit?.coursePointer?.courseCode,
    unit?.coursePointer?.course_code,
  ]

  return courseCodeCandidates.some(
    code => typeof code === 'string' && code.toLowerCase().includes('_dwb_'),
  )
}

export function getCourseHeaderData({ unit, linkSummaries, productName }) {
  const totalAmount = Array.isArray(linkSummaries) ? linkSummaries.length : 0
  const totalComplete = Array.isArray(linkSummaries)
    ? linkSummaries.filter(summary => summary?.lessonComplete).length
    : 0
  const isDwbUnit = isDigitalWellBeingUnit({ unit, productName })

  const lessonsAmounts = [
    {
      totalAmount,
      totalComplete,
      categoryLabel: isDwbUnit ? 'Digital Well-Being' : '',
    },
  ]

  const scopeAndSequenceLinks = (unit?.courseContent?.coursePdf || []).map(
    coursePdf => {
      const displayTitle = coursePdf?.displayTitle ?? ''
      const suffix = isDwbUnit ? '(Digital Well-Being Unit)' : ''

      return {
        linkLabel: !suffix || displayTitle.includes(suffix)
          ? displayTitle
          : `${displayTitle} ${suffix}`.trim(),
        linkHref: coursePdf?.file?.url ?? '',
      }
    },
  )

  return { lessonsAmounts, scopeAndSequenceLinks }
}

export function getUnitTabs(unit) {
  return Array.isArray(unit?.courseContent?.tabSummaries)
    ? unit.courseContent.tabSummaries
    : []
}

function resolveDwbCourseCodeFromGrade(gradeSegment) {
  if (typeof gradeSegment !== 'string') return null
  if (!gradeSegment.startsWith('grade-')) return null

  const normalizedGrade = gradeSegment.replace('grade-', '').toLowerCase()
  if (!/^(k|\d{1,2})$/.test(normalizedGrade)) return null

  return `${normalizedGrade}_dwb_tier-1_siteEducator`
}

export function resolveCourseCode({
  coursePointer,
  unit,
  gradeSegment,
  contentGroupCode,
}) {
  const resolvedFromPayload =
    coursePointer?.courseCode ||
    coursePointer?.course_code ||
    coursePointer?.code ||
    unit?.courseCode ||
    unit?.course_code ||
    unit?.courseContent?.courseCode ||
    unit?.courseContent?.course_code

  if (resolvedFromPayload) return resolvedFromPayload
  if (contentGroupCode !== 'dwb') return null

  return resolveDwbCourseCodeFromGrade(gradeSegment)
}

export function selectClassRegistrationFromResponse({
  instructorResponse,
  classId,
  siteId,
  gradeSegment,
  courseCode,
}) {
  if (!instructorResponse || typeof instructorResponse !== 'object') return null

  const registrationCandidates = Object.values(instructorResponse).flatMap(
    programScopes => {
      if (!programScopes || typeof programScopes !== 'object') return []
      return Object.values(programScopes).flatMap(scopeValue =>
        Array.isArray(scopeValue) ? scopeValue : [scopeValue],
      )
    },
  )

  if (!registrationCandidates.length) return null

  const siteIdNumber = Number(siteId)
  const siteMatchedCandidates = registrationCandidates.filter(candidate => {
    const candidateSiteId = Number(candidate?.site_id)
    return (
      !Number.isNaN(siteIdNumber) &&
      !Number.isNaN(candidateSiteId) &&
      candidateSiteId === siteIdNumber
    )
  })

  const scopedCandidates = siteMatchedCandidates.length
    ? siteMatchedCandidates
    : registrationCandidates

  const normalizedGrade =
    typeof gradeSegment === 'string' && gradeSegment.startsWith('grade-')
      ? gradeSegment.replace('grade-', '').toLowerCase()
      : null

  const extractedGradeFromCourseCode =
    typeof courseCode !== 'string'
      ? null
      : courseCode.match(/g(k|\d{1,2})/i)?.[1]?.toLowerCase() ||
        courseCode
          .match(/^(k|\d{1,2})_core_tier-1_siteEducator$/i)?.[1]
          ?.toLowerCase() ||
        courseCode
          .match(/^(k|\d{1,2})_dwb_tier-1_siteEducator$/i)?.[1]
          ?.toLowerCase() ||
        null

  const targetCourseNames = new Set(
    [normalizedGrade, extractedGradeFromCourseCode]
      .filter(Boolean)
      .map(value => value.toLowerCase()),
  )

  const courseMatchedCandidates =
    targetCourseNames.size > 0
      ? scopedCandidates.filter(candidate =>
          targetCourseNames.has(String(candidate?.course_name || '').toLowerCase()),
        )
      : scopedCandidates

  if (!classId) {
    if (courseMatchedCandidates.length > 0) {
      return courseMatchedCandidates[0]
    }
    // When grade is explicit but no course match exists, avoid
    // attaching completion from an unrelated grade/class.
    if (targetCourseNames.size > 0) {
      return null
    }
    return scopedCandidates[0] || null
  }

  const classIdNumber = Number(classId)
  return (
    (courseMatchedCandidates.length
      ? courseMatchedCandidates
      : scopedCandidates
    ).find(candidate => {
      const registrationId = Number(candidate?.registration_id)
      return (
        !Number.isNaN(classIdNumber) &&
        !Number.isNaN(registrationId) &&
        registrationId === classIdNumber
      )
    }) || null
  )
}

export function applyLessonCompletionToLinkSummaries({
  linkSummaries,
  classRegistration,
}) {
  if (!Array.isArray(linkSummaries)) return []
  if (!classRegistration?.lesson_nodes) return linkSummaries

  return linkSummaries.map(summary => {
    const courseNodeEntryPoint =
      summary?.courseNodeConnector?.courseNodeEntryPoint ||
      summary?.courseNodeConnector?.id ||
      null

    if (!courseNodeEntryPoint) {
      return { ...summary, lessonComplete: false, isPreview: false }
    }

    const lessonComplete = classRegistration.lesson_nodes.some(lessonNode => {
      const leafNodeId = lessonNode?.leaf_node_id
      return (
        typeof leafNodeId === 'string' &&
        leafNodeId.includes(courseNodeEntryPoint) &&
        lessonNode?.is_done === true
      )
    })

    return { ...summary, lessonComplete, isPreview: false }
  })
}
